import { createTheme } from '@mui/material/styles';

// Palette de couleurs tech moderne et sobre
const colors = {
  // Couleurs principales - inspiration tech professionnelle
  primary: {
    main: '#2563eb', // Bleu moderne et professionnel
    light: '#60a5fa',
    dark: '#1d4ed8',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#7c3aed', // Violet subtil et élégant
    light: '#a78bfa',
    dark: '#5b21b6',
    contrastText: '#ffffff',
  },
  // Couleurs d'accent discrètes
  accent: {
    purple: '#6366f1', // Indigo moderne
    blue: '#0ea5e9',   // Bleu sky
    teal: '#14b8a6',   // Teal équilibré
    orange: '#f97316', // Orange doux
    gray: '#64748b',   // Gris moderne
  },
  // Backgrounds élégants
  background: {
    default: '#0f172a', // Slate très sombre
    paper: '#1e293b',   // Slate sombre pour les cartes
    surface: '#334155', // Surface intermédiaire
  },
  // Textes optimisés pour la lisibilité
  text: {
    primary: '#f8fafc',
    secondary: '#cbd5e1',
    disabled: '#64748b',
  },
};

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: colors.primary,
    secondary: colors.secondary,
    background: colors.background,
    text: colors.text,
    // Couleurs custom pour les gradients
    info: {
      main: colors.accent.blue,
    },
    success: {
      main: colors.accent.teal,
    },
    warning: {
      main: colors.accent.orange,
    },
    error: {
      main: colors.accent.purple,
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Segoe UI", sans-serif',
    h1: {
      fontSize: '3rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
      color: colors.text.primary,
    },
    h2: {
      fontSize: '2.25rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      background: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.secondary.light} 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    h3: {
      fontSize: '1.875rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      color: colors.text.primary,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: colors.text.primary,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.7,
      color: colors.text.secondary,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: colors.text.secondary,
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
    '0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23)',
    '0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23)',
    '0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22)',
    '0 19px 38px rgba(0, 0, 0, 0.30), 0 15px 12px rgba(0, 0, 0, 0.22)',
    '0 1px 3px rgba(37, 99, 235, 0.08), 0 1px 2px rgba(0, 0, 0, 0.24)',
    '0 3px 6px rgba(37, 99, 235, 0.1), 0 3px 6px rgba(0, 0, 0, 0.23)',
    '0 10px 20px rgba(37, 99, 235, 0.12), 0 6px 6px rgba(0, 0, 0, 0.23)',
    '0 14px 28px rgba(37, 99, 235, 0.15), 0 10px 10px rgba(0, 0, 0, 0.22)',
    '0 19px 38px rgba(37, 99, 235, 0.18), 0 15px 12px rgba(0, 0, 0, 0.22)',
    '0 24px 48px rgba(37, 99, 235, 0.20), 0 18px 15px rgba(0, 0, 0, 0.22)',
    '0 1px 3px rgba(124, 58, 237, 0.08), 0 1px 2px rgba(0, 0, 0, 0.24)',
    '0 3px 6px rgba(124, 58, 237, 0.1), 0 3px 6px rgba(0, 0, 0, 0.23)',
    '0 10px 20px rgba(124, 58, 237, 0.12), 0 6px 6px rgba(0, 0, 0, 0.23)',
    '0 14px 28px rgba(124, 58, 237, 0.15), 0 10px 10px rgba(0, 0, 0, 0.22)',
    '0 19px 38px rgba(124, 58, 237, 0.18), 0 15px 12px rgba(0, 0, 0, 0.22)',
    '0 24px 48px rgba(124, 58, 237, 0.20), 0 18px 15px rgba(0, 0, 0, 0.22)',
    '0 30px 60px rgba(37, 99, 235, 0.22), 0 20px 18px rgba(0, 0, 0, 0.22)',
    '0 35px 70px rgba(37, 99, 235, 0.24), 0 22px 20px rgba(0, 0, 0, 0.22)',
    '0 40px 80px rgba(37, 99, 235, 0.26), 0 24px 22px rgba(0, 0, 0, 0.22)',
    '0 45px 90px rgba(37, 99, 235, 0.28), 0 26px 24px rgba(0, 0, 0, 0.22)',
    '0 50px 100px rgba(37, 99, 235, 0.30), 0 28px 26px rgba(0, 0, 0, 0.22)',
    '0 55px 110px rgba(37, 99, 235, 0.32), 0 30px 28px rgba(0, 0, 0, 0.22)',
    '0 60px 120px rgba(37, 99, 235, 0.34), 0 32px 30px rgba(0, 0, 0, 0.22)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: `0 4px 12px rgba(37, 99, 235, 0.2)`,
          },
        },
        contained: {
          background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
          boxShadow: `0 4px 14px rgba(37, 99, 235, 0.2)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.secondary.light} 100%)`,
            boxShadow: `0 6px 20px rgba(37, 99, 235, 0.25)`,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: colors.background.paper,
          border: `1px solid rgba(100, 116, 139, 0.2)`,
          borderRadius: 16,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            border: `1px solid rgba(37, 99, 235, 0.3)`,
            boxShadow: `0 8px 32px rgba(37, 99, 235, 0.1)`,
            transform: 'translateY(-2px)',
          },
        },
      },
    },
  },
});

// Export des couleurs pour utilisation dans les composants
export { colors };
